import * as React from "react";
import {
  Avatar,
  Text,
  Caption1,
  Body1,
  Button,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Edit20Regular, Delete20Regular } from "@fluentui/react-icons";
import { DiscussionCommentDto } from "../../../services/discussions.service";
import RichTextDisplay from "./RichTextDisplay";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  avatar: {
    flexShrink: 0,
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    flex: 1,
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
  },
  text: {
    color: tokens.colorNeutralForeground1,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  statusIndicator: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    marginLeft: "auto",
  },
  archivedText: {
    color: tokens.colorNeutralForeground4,
    fontStyle: "italic",
  },
});

interface CommentItemProps {
  comment: DiscussionCommentDto;
  isEditable?: boolean;
  isArchivable?: boolean;
  onEditClick?: (comment: DiscussionCommentDto) => void;
  onArchiveClick?: (comment: DiscussionCommentDto) => void;
}

// Helper to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return "just now";
  } else if (diffMins < 60) {
    return diffMins === 1 ? "1 minute ago" : `${diffMins} minutes ago`;
  } else if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  } else if (diffDays < 7) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Helper to get initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Check if a comment has been edited (updatedAt differs from createdAt by more than 1 second)
const isEdited = (comment: DiscussionCommentDto): boolean => {
  const created = new Date(comment.createdAt).getTime();
  const updated = new Date(comment.updatedAt).getTime();
  return updated - created > 1000;
};

const CommentItem: React.FC<CommentItemProps> = ({ comment, isEditable, isArchivable, onEditClick, onArchiveClick }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  return (
    <div className={styles.container}>
      <Avatar
        className={styles.avatar}
        name={comment.createdByName}
        size={32}
        initials={getInitials(comment.createdByName)}
        color="colorful"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <Text className={typography.formLabel}>
            {comment.createdByName}
          </Text>
          <Caption1 className={styles.timestamp}>
            {formatDate(comment.createdAt)}
            {!comment.isArchived && isEdited(comment) && " (edited)"}
          </Caption1>
          {isEditable && (
            <Tooltip content="Edit comment" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit20Regular />}
                onClick={() => onEditClick?.(comment)}
              />
            </Tooltip>
          )}
          {isArchivable && (
            <Tooltip content="Archive comment" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete20Regular />}
                onClick={() => onArchiveClick?.(comment)}
              />
            </Tooltip>
          )}
          {comment.statusAfter && comment.statusAfter !== comment.statusBefore && (
            <div className={styles.statusIndicator}>
              {comment.statusBefore || "None"} → {comment.statusAfter}
            </div>
          )}
        </div>
        {comment.isArchived ? (
          <Body1 className={styles.archivedText}>
            Comment archived
          </Body1>
        ) : (
          <Body1 className={styles.text}>
            {comment.commentContent ? (
              <RichTextDisplay markup={comment.commentContent} />
            ) : (
              comment.commentText
            )}
          </Body1>
        )}
      </div>
    </div>
  );
};

export default CommentItem;
